import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2 } from "lucide-react";
import { api } from "../services/api";
import toast from "react-hot-toast";
import { useAuth } from "../contexts/AuthContext";

interface BirthDetails {
  dob: string;
  birthTime: string;
  birthPlace: string;
  gender: string;
}

interface BirthDetailsPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (details: BirthDetails) => void;
  category?: string;
  subCategory?: string;
  mainCategory?: string;
}

const BirthDetailsPopup: React.FC<BirthDetailsPopupProps> = ({
  isOpen,
  onClose,
  onSave,
  category,
  subCategory,
  mainCategory,
}) => {
  const isJapanLane = mainCategory === "Astria Japan";
  const isKoreaLane = mainCategory === "Astria Korea";
  const preferredLanguage =
    typeof window !== "undefined"
      ? localStorage.getItem("preferredLanguage") || "en"
      : "en";
  const dateFormat = preferredLanguage === "th" ? "DD/MM/YYYY" : "MM/DD/YYYY";

  const [formData, setFormData] = useState<BirthDetails>({
    dob: "",
    birthTime: "",
    birthPlace: "",
    gender: "",
  });

  // Split fields for Japan/Korea graph-style DOB input
  const [splitDOB, setSplitDOB] = useState({ yyyy: "", mm: "", dd: "" });
  const [splitTime, setSplitTime] = useState({ hh: "", mm: "" });

  const [errors, setErrors] = useState<Partial<BirthDetails>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();
  const isTester = user?.roleId === 3;

  // Load existing data from localStorage on mount
  useEffect(() => {
    if (category === "LifeGraph") {
    } else {
      const savedDetails = localStorage.getItem("userBirthDetails");
      if (savedDetails) {
        try {
          const parsed = JSON.parse(savedDetails);
          setFormData((prev) => ({ ...prev, ...parsed }));
        } catch (e) {
          console.error("Failed to parse birth details");
        }
      }
    }
  }, []);

  // Auto-format date as user types
  const formatDate = (value: string): string => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, "");

    // Format as DD/MM/YYYY
    if (digits.length <= 2) {
      return digits;
    } else if (digits.length <= 4) {
      return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    } else if (digits.length <= 8) {
      return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
    }

    // Max 8 digits (DDMMYYYY)
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
  };

  // Auto-format time as user types
  const formatTime = (value: string): string => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, "");

    // Format as HH:MM
    if (digits.length <= 2) {
      return digits;
    } else if (digits.length <= 4) {
      return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
    }

    // Max 4 digits (HHMM)
    return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<BirthDetails> = {};

    if (!formData.dob) {
      newErrors.dob = "Date of birth is required";
    } else {
      // Validate date format based on preferred language
      const dateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
      if (!dateRegex.test(formData.dob)) {
        newErrors.dob = `Please use format ${dateFormat}`;
      } else {
        const [part1, part2, year] = formData.dob.split("/").map(Number);
        const isThai = preferredLanguage === "th";
        const day = isThai ? part1 : part2;
        const month = isThai ? part2 : part1;

        if (month < 1 || month > 12 || day < 1 || day > 31) {
          newErrors.dob = "Please enter a valid date";
        } else {
          const date = new Date(year, month - 1, day);
          if (
            date.getFullYear() !== year ||
            date.getMonth() !== month - 1 ||
            date.getDate() !== day
          ) {
            newErrors.dob = "Please enter a valid date";
          }

          // Check if date is not in the future
          const today = new Date();
          const inputDate = new Date(year, month - 1, day);
          if (inputDate > today) {
            newErrors.dob = "Date cannot be in the future";
          }
        }
      }
    }

    // Validate time format if provided (HH:MM)
    if (formData.birthTime) {
      const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
      if (!timeRegex.test(formData.birthTime)) {
        newErrors.birthTime = "Please use format HH:MM (24-hour)";
      } else {
        // Validate hour range
        const [hours] = formData.birthTime.split(":").map(Number);
        if (hours < 0 || hours > 23) {
          newErrors.birthTime = "Hours must be between 00-23";
        }
      }
    }

    // Gender is required
    if (!formData.gender) {
      newErrors.gender = "Please select a gender";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      setIsSubmitting(true);
      try {
        // Save to localStorage
        localStorage.setItem("userBirthDetails", JSON.stringify(formData));

        // Update user in database
        await api.put(`/backend/user/${user?._id}`, {
          dob: formData.dob,
          dob_time: formData.birthTime,
          dob_place: formData.birthPlace,
          gender: formData.gender,
        });

        toast.success("Birth details saved successfully!");
        onSave(formData);
        onClose();
      } catch (error) {
        console.error("Failed to save birth details:", error);
        toast.error("Failed to save birth details. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    let formattedValue = value;

    // Apply auto-formatting based on field
    if (name === "dob") {
      formattedValue = formatDate(value);
    } else if (name === "birthTime") {
      formattedValue = formatTime(value);
    }

    setFormData((prev) => ({ ...prev, [name]: formattedValue }));

    // Clear error for this field
    if (errors[name as keyof BirthDetails]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  // Handle key down for backspace to allow deletion
  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    field: string,
  ) => {
    const { key } = e;
    const { value } = e.currentTarget;

    // Allow backspace to work normally
    if (key === "Backspace") {
      // If we're at a separator position, remove one more character
      if (field === "dob" && (value.length === 3 || value.length === 6)) {
        e.preventDefault();
        const newValue = value.slice(0, -1);
        setFormData((prev) => ({ ...prev, [field]: newValue }));
      } else if (field === "birthTime" && value.length === 3) {
        e.preventDefault();
        const newValue = value.slice(0, -1);
        setFormData((prev) => ({ ...prev, [field]: newValue }));
      }
    }
  };

  // Handler for Japan/Korea graph-style split DOB boxes
  const handleSplitDOBChange = (field: "yyyy" | "mm" | "dd", value: string) => {
    const digits = value.replace(/\D/g, "");
    const maxLen = field === "yyyy" ? 4 : 2;
    const trimmed = digits.slice(0, maxLen);
    const next = { ...splitDOB, [field]: trimmed };
    setSplitDOB(next);
    if (next.dd && next.mm && next.yyyy.length === 4) {
      setFormData((prev) => ({
        ...prev,
        dob: `${next.dd.padStart(2, "0")}/${next.mm.padStart(2, "0")}/${next.yyyy}`,
      }));
    }
    if (errors.dob) setErrors((prev) => ({ ...prev, dob: undefined }));
  };

  const handleSplitTimeChange = (field: "hh" | "mm", value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 2);
    const next = { ...splitTime, [field]: digits };
    setSplitTime(next);
    if (next.hh || next.mm) {
      setFormData((prev) => ({
        ...prev,
        birthTime: `${next.hh.padStart(2, "0")}:${next.mm.padStart(2, "0")}`,
      }));
    }
    if (errors.birthTime)
      setErrors((prev) => ({ ...prev, birthTime: undefined }));
  };

  const handleGenderSelect = (value: string) => {
    setFormData((prev) => ({ ...prev, gender: value }));
    if (errors.gender) setErrors((prev) => ({ ...prev, gender: undefined }));
  };

  const getTitle = () => {
    switch (category) {
      case "love":
        return "❤️ Love Reading";
      case "work":
        return "💼 Work Reading";
      case "money":
        return "💰 Money Reading";
      case "healjai":
        return `🌿 Astria Reading`;
      case "LifeGraph":
        return "📈 LifeGraph Reading";
      case "ThaiAstro":
        return "📈 ThaiAstro Reading";
      case "Uranian":
        return "📈 Uranian Reading";
      default:
        return "🔮 Tarot Reading";
    }
  };

  // Testers see the actual module name (subcategory, falling back to the
  // main category) instead of the generic emoji-based titles above.
  const applyTesterBranding = (label?: string) =>
    label ? label.replace(/Astria/gi, "Cosmic") : label;

  const getTesterTitle = () => {
    const moduleName = subCategory || category || mainCategory;
    return applyTesterBranding(moduleName) || "Reading";
  };

  const displayTitle = isTester ? getTesterTitle() : getTitle();

  // ── Japan / Korea graph-style DOB input layout ──────────────────────────────
  if (isJapanLane || isKoreaLane) {
    const fontFamily = isJapanLane
      ? "'Noto Sans JP', sans-serif"
      : "'Noto Sans KR', sans-serif";
    const header = isJapanLane
      ? "日本占星術 · 誕生データ入力"
      : "한국 점성술 · 생년월일 입력";
    const subCopy = isJapanLane
      ? "静かに教えていただけますか。"
      : "조용히 몇 가지 정보를 여쭤봐도 될까요.";
    const labelDOB = isJapanLane ? "生年月日" : "생년월일";
    const helperDOB = isJapanLane
      ? "あなたの生まれた日を、静かに教えてください。"
      : "태어난 날을 조용히 알려주세요.";
    const labelTime = isJapanLane
      ? "出生時間（わかれば）"
      : "출생시간（가능하다면）";
    const helperTime = isJapanLane
      ? "時間がわからない場合は、空欄のままで大丈夫です。"
      : "시간이 기억나지 않으면 비워두셔도 됩니다.";
    const labelPlace = isJapanLane ? "出生地" : "출생지";
    const helperPlace = isJapanLane
      ? "生まれた場所を、できる範囲でお知らせください。"
      : "태어난 곳을 알 수 있는 범위에서 알려주세요.";
    const labelGender = isJapanLane ? "性別" : "성별";
    const helperGender = isJapanLane
      ? "より正確な占いのためにお聞きしています。"
      : "더 정확한 분석을 위해 여쭤봅니다.";
    const genderOptions = isJapanLane
      ? [
          { value: "male", label: "男性" },
          { value: "female", label: "女性" },
          { value: "other", label: "その他" },
        ]
      : [
          { value: "male", label: "남성" },
          { value: "female", label: "여성" },
          { value: "other", label: "기타" },
        ];
    const ctaLabel = isJapanLane ? "解析をはじめる" : "흐름을 열어드릴게요";

    const inputStyle: React.CSSProperties = {
      height: 48,
      borderRadius: 12,
      border: "1px solid rgba(255,255,255,0.13)",
      background: "rgba(255,255,255,0.06)",
      color: "#fff",
      padding: "0 12px",
      fontSize: 16,
      outline: "none",
      fontFamily,
      width: "100%",
      boxSizing: "border-box" as const,
    };

    const labelStyle: React.CSSProperties = {
      fontSize: 14,
      color: "rgba(255,255,255,0.9)",
      marginBottom: 6,
      display: "block",
      fontFamily,
    };

    const helperStyle: React.CSSProperties = {
      fontSize: 13,
      color: "rgba(255,255,255,0.55)",
      marginTop: 5,
      fontFamily,
    };

    return (
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999]"
            />
            <div className="fixed inset-0 flex items-center justify-center z-[99999] pointer-events-none">
              <motion.div
                initial={{ opacity: 0, scale: 0.93, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.93, y: 20 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                className="pointer-events-auto"
                style={{
                  width: "90%",
                  maxWidth: 390,
                  maxHeight: "90vh",
                  overflowY: "auto",
                  scrollbarWidth: "none",
                  borderRadius: 20,
                  background:
                    "linear-gradient(160deg, #2d1b4e 0%, #1a0f2e 100%)",
                  padding: 24,
                  boxShadow: "0 8px 40px rgba(167,139,250,0.18)",
                  fontFamily,
                  position: "relative",
                }}
              >
                {/* Close */}
                <button
                  onClick={onClose}
                  type="button"
                  style={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "rgba(255,255,255,0.6)",
                    padding: 4,
                  }}
                >
                  <X size={18} />
                </button>

                {/* Header */}
                <div style={{ marginBottom: 24, paddingRight: 24 }}>
                  <p
                    style={{
                      fontSize: 20,
                      fontWeight: 500,
                      color: "#fff",
                      marginBottom: 6,
                      fontFamily,
                    }}
                  >
                    {header}
                  </p>
                  <p
                    style={{
                      fontSize: 13,
                      color: "rgba(255,255,255,0.55)",
                      fontFamily,
                    }}
                  >
                    {subCopy}
                  </p>
                </div>

                <form
                  onSubmit={handleSubmit}
                  style={{ display: "flex", flexDirection: "column", gap: 20 }}
                >
                  {/* DOB — graph boxes YYYY / MM / DD */}
                  <div>
                    <label style={labelStyle}>{labelDOB}</label>
                    <div style={{ display: "flex", gap: 8 }}>
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="YYYY"
                        maxLength={4}
                        value={splitDOB.yyyy}
                        onChange={(e) =>
                          handleSplitDOBChange("yyyy", e.target.value)
                        }
                        style={{ ...inputStyle, flex: 2 }}
                        onFocus={(e) => {
                          e.currentTarget.style.boxShadow =
                            "0 0 0 2px rgba(167,139,250,0.45)";
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.boxShadow = "none";
                        }}
                      />
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="MM"
                        maxLength={2}
                        value={splitDOB.mm}
                        onChange={(e) =>
                          handleSplitDOBChange("mm", e.target.value)
                        }
                        style={{ ...inputStyle, flex: 1 }}
                        onFocus={(e) => {
                          e.currentTarget.style.boxShadow =
                            "0 0 0 2px rgba(167,139,250,0.45)";
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.boxShadow = "none";
                        }}
                      />
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="DD"
                        maxLength={2}
                        value={splitDOB.dd}
                        onChange={(e) =>
                          handleSplitDOBChange("dd", e.target.value)
                        }
                        style={{ ...inputStyle, flex: 1 }}
                        onFocus={(e) => {
                          e.currentTarget.style.boxShadow =
                            "0 0 0 2px rgba(167,139,250,0.45)";
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.boxShadow = "none";
                        }}
                      />
                    </div>
                    {errors.dob && (
                      <p
                        style={{
                          color: "#f87171",
                          fontSize: 12,
                          marginTop: 4,
                          fontFamily,
                        }}
                      >
                        {errors.dob}
                      </p>
                    )}
                    <p style={helperStyle}>{helperDOB}</p>
                  </div>

                  {/* Birth Time — HH : MM */}
                  <div>
                    <label style={labelStyle}>{labelTime}</label>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="HH"
                        maxLength={2}
                        value={splitTime.hh}
                        onChange={(e) =>
                          handleSplitTimeChange("hh", e.target.value)
                        }
                        style={{ ...inputStyle, flex: 1 }}
                        onFocus={(e) => {
                          e.currentTarget.style.boxShadow =
                            "0 0 0 2px rgba(167,139,250,0.45)";
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.boxShadow = "none";
                        }}
                      />
                      <span
                        style={{
                          color: "rgba(255,255,255,0.5)",
                          fontSize: 20,
                          fontWeight: 300,
                        }}
                      >
                        :
                      </span>
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="MM"
                        maxLength={2}
                        value={splitTime.mm}
                        onChange={(e) =>
                          handleSplitTimeChange("mm", e.target.value)
                        }
                        style={{ ...inputStyle, flex: 1 }}
                        onFocus={(e) => {
                          e.currentTarget.style.boxShadow =
                            "0 0 0 2px rgba(167,139,250,0.45)";
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.boxShadow = "none";
                        }}
                      />
                    </div>
                    {errors.birthTime && (
                      <p
                        style={{
                          color: "#f87171",
                          fontSize: 12,
                          marginTop: 4,
                          fontFamily,
                        }}
                      >
                        {errors.birthTime}
                      </p>
                    )}
                    <p style={helperStyle}>{helperTime}</p>
                  </div>

                  {/* Birth Place — free text */}
                  <div>
                    <label style={labelStyle}>{labelPlace}</label>
                    <input
                      type="text"
                      name="birthPlace"
                      value={formData.birthPlace}
                      onChange={handleChange}
                      placeholder={
                        isJapanLane ? "都道府県 / 市区町村" : "도시 / 지역"
                      }
                      style={inputStyle}
                      onFocus={(e) => {
                        e.currentTarget.style.boxShadow =
                          "0 0 0 2px rgba(167,139,250,0.45)";
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    />
                    <p style={helperStyle}>{helperPlace}</p>
                  </div>

                  {/* Gender — pill selector */}
                  <div>
                    <label style={labelStyle}>{labelGender}</label>
                    <div style={{ display: "flex", gap: 8 }}>
                      {genderOptions.map((opt) => {
                        const isSelected = formData.gender === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleGenderSelect(opt.value)}
                            style={{
                              flex: 1,
                              height: 44,
                              borderRadius: 12,
                              border: isSelected
                                ? "1px solid rgba(167,139,250,0.8)"
                                : "1px solid rgba(255,255,255,0.13)",
                              background: isSelected
                                ? "linear-gradient(135deg, #c4b5fd 0%, #7c3aed 100%)"
                                : "rgba(255,255,255,0.06)",
                              color: "#fff",
                              fontSize: 14,
                              fontFamily,
                              cursor: "pointer",
                              transition: "all 0.2s",
                            }}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                    {errors.gender && (
                      <p
                        style={{
                          color: "#f87171",
                          fontSize: 12,
                          marginTop: 4,
                          fontFamily,
                        }}
                      >
                        {errors.gender}
                      </p>
                    )}
                    <p style={helperStyle}>{helperGender}</p>
                  </div>

                  {/* CTA Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={{
                      height: 52,
                      borderRadius: 14,
                      background:
                        "linear-gradient(135deg, #c4b5fd 0%, #7c3aed 100%)",
                      color: "#fff",
                      fontSize: 16,
                      fontWeight: 500,
                      border: "none",
                      cursor: isSubmitting ? "not-allowed" : "pointer",
                      opacity: isSubmitting ? 0.6 : 1,
                      boxShadow: "0 0 12px rgba(167,139,250,0.35)",
                      fontFamily,
                      transition: "opacity 0.2s",
                    }}
                  >
                    {isSubmitting ? (
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 8,
                        }}
                      >
                        <Loader2
                          size={16}
                          style={{ animation: "spin 1s linear infinite" }}
                        />
                        {isJapanLane ? "保存中..." : "저장 중..."}
                      </span>
                    ) : (
                      ctaLabel
                    )}
                  </button>
                </form>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    );
  }
  // ── END Japan / Korea layout ─────────────────────────────────────────────────

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop - covers entire viewport */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999]"
          />

          {/* Popup Container - fixed positioning with flex centering */}
          <div className="fixed inset-0 flex items-center justify-center z-[99999] pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="w-[90%] max-w-md bg-white rounded-3xl shadow-2xl pointer-events-auto"
              style={{
                maxHeight: "90vh",
                overflowY: "auto",
                scrollbarWidth: "none" /* Firefox */,
                msOverflowStyle: "none" /* IE and Edge */,
              }}
            >
              {/* Hide scrollbar for Chrome, Safari and Opera */}
              <style>{`
                .w-\\[90\\%\\]::-webkit-scrollbar {
                  display: none;
                }
              `}</style>

              {/* Header */}
              <div className="relative bg-gradient-to-r from-[#6D4C92] to-[#44304e] p-6 text-white rounded-t-3xl">
                <button
                  onClick={onClose}
                  className="absolute right-4 top-4 text-white/80 hover:text-white transition-colors"
                  type="button"
                >
                  <X className="w-5 h-5" />
                </button>
                <h2 className="text-2xl font-semibold mb-1">
                  {displayTitle}
                </h2>
                <p className="text-white/80 text-sm">
                  Please share your birth details for a personalized reading
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-6 space-y-5">
                {/* DOB - Required */}
                <div>
                  <label className="block text-sm font-medium text-[#4A3F35] mb-1">
                    Date of Birth <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="dob"
                    value={formData.dob}
                    onChange={handleChange}
                    onKeyDown={(e) => handleKeyDown(e, "dob")}
                    placeholder={dateFormat}
                    maxLength={10} // DD/MM/YYYY = 10 characters
                    className={`w-full px-4 py-3 rounded-xl border ${
                      errors.dob ? "border-red-300" : "border-[#E8E3DA]"
                    } focus:outline-none focus:ring-2 focus:ring-[#6D4C92]/20 focus:border-[#6D4C92] transition-colors`}
                  />
                  {errors.dob && (
                    <p className="mt-1 text-sm text-red-500">{errors.dob}</p>
                  )}
                  <p className="mt-1 text-xs text-[#8A7F73]">
                    Required for your birth chart • Auto-formats as you type
                  </p>
                </div>

                {/* Gender - Required */}
                <div>
                  <label className="block text-sm font-medium text-[#4A3F35] mb-1">
                    Gender <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-xl border ${
                      errors.gender ? "border-red-300" : "border-[#E8E3DA]"
                    } focus:outline-none focus:ring-2 focus:ring-[#6D4C92]/20 focus:border-[#6D4C92] transition-colors bg-white`}
                  >
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.gender && (
                    <p className="mt-1 text-sm text-red-500">{errors.gender}</p>
                  )}
                  <p className="mt-1 text-xs text-[#8A7F73]">
                    Helps refine your chart calculations
                  </p>
                </div>

                {/* Birth Time - Optional */}
                <div>
                  <label className="block text-sm font-medium text-[#4A3F35] mb-1">
                    Birth Time{" "}
                    <span className="text-[#8A7F73]">(optional)</span>
                  </label>
                  <input
                    type="text"
                    name="birthTime"
                    value={formData.birthTime}
                    onChange={handleChange}
                    onKeyDown={(e) => handleKeyDown(e, "birthTime")}
                    placeholder="HH:MM (24-hour format)"
                    maxLength={5} // HH:MM = 5 characters
                    className={`w-full px-4 py-3 rounded-xl border ${
                      errors.birthTime ? "border-red-300" : "border-[#E8E3DA]"
                    } focus:outline-none focus:ring-2 focus:ring-[#6D4C92]/20 focus:border-[#6D4C92] transition-colors`}
                  />
                  {errors.birthTime && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.birthTime}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-[#8A7F73]">
                    Adds precision to your reading • Auto-formats as you type
                  </p>
                </div>

                {/* Birth Place - Optional */}
                <div>
                  <label className="block text-sm font-medium text-[#4A3F35] mb-1">
                    Birth Place{" "}
                    <span className="text-[#8A7F73]">(optional)</span>
                  </label>
                  <input
                    type="text"
                    name="birthPlace"
                    value={formData.birthPlace}
                    onChange={handleChange}
                    placeholder="City, Country"
                    className="w-full px-4 py-3 rounded-xl border border-[#E8E3DA] focus:outline-none focus:ring-2 focus:ring-[#6D4C92]/20 focus:border-[#6D4C92] transition-colors"
                  />
                  <p className="mt-1 text-xs text-[#8A7F73]">
                    Helps calculate your rising sign
                  </p>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-gradient-to-r from-[#6D4C92] to-[#44304e]
                    text-white font-medium rounded-xl
                    hover:shadow-lg hover:shadow-[#6D4C92]/20
                    transition-all duration-300
                    transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
                    focus:outline-none focus:ring-2 focus:ring-[#6D4C92]/50"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </div>
                  ) : (
                    "Continue to Your Reading ✨"
                  )}
                </button>
              </form>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default BirthDetailsPopup;
